import * as api from "./middleware/api.middleware";
import type {
  IDataMartRecord,
  ITargetEntityDmColumnRecord,
  ITargetEntityDmRecord,
  ITargetEntityDmRelationshipRecord
} from "../interfaces/dataMartModelling.interfaces";
import {
  buildDataMartModel,
  type BuildDataMartModelParams
} from "../containers/dataMartMapping/model/buildModel";
import { typedRequest } from "./middleware/request";
import type { GenerationReport } from "../components/modelling/GenerationReportSummary";
import { getScopeCustomerId, getScopeEnvironmentId } from "./activeScope";

const resolveApiBaseUrl = () => {
  const envBase = (process.env.REACT_APP_API_URL as string | undefined)?.trim();
  if (envBase) {
    return envBase.replace(/\/$/, "");
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/\/$/, "");
  }
  return "";
};

const API_BASE_URL = resolveApiBaseUrl();
const RELATED_ENTITIES_OVERRIDE = (process.env.REACT_APP_RELATED_ENTITIES_API as string | undefined)?.trim();

const getAccessToken = () => {
  const accessToken = localStorage.getItem("access_token");
  return {
    Authorization: `Bearer ${accessToken}`
  };
};

const getEnvironmentId = () => getScopeEnvironmentId();
const getCustomerId = () => getScopeCustomerId();

export type DataMartMetadataResponse = {
  data_marts: IDataMartRecord[];
  target_entities: ITargetEntityDmRecord[];
  target_entity_columns: ITargetEntityDmColumnRecord[];
};

export type DataMartSqlPreviewResponse = {
  target_path: string | null;
  sql: string;
  model_generation_id: string;
  generated_stored_procedures: Record<string, unknown>;
  is_onprem: boolean;
  /** Per-entity coverage of the run — which tables were generated, skipped, failed. */
  generation_report?: GenerationReport | null;
};

export type TargetEntityRdvRecord = {
  target_entity_rdv_id: number;
  target_entity_rdv_name: string;
  table_construct?: string | null;
  target_schema?: string | null;
  subject_area?: string | null;
  generate_model_entity?: boolean | null;
  /** Columns modelled on the entity; 0 marks a migration leftover. */
  column_count?: number | null;
};

export type DataMartColumnAutocompleteResponse = {
  target_entity_rdv_name?: string | null;
  target_entity_rdv_source_column_names?: string[] | null;
  target_entity_rdv_modeled_column_names?: string[] | null;
  target_entity_rdv_column_names?: string[] | null;
};

export type DataMartSourceColumnRecord = {
  source_target_entity_rdv_column_mapping_id: number | null;
  source_entity_column_id: number | null;
  source_entity_column_name: string | null;
  source_entity_id: number | null;
  source_entity_name: string | null;
  target_entity_rdv_id: number | null;
  target_entity_rdv_name: string | null;
  target_schema: string | null;
  target_entity_rdv_column_id: number | null;
  target_entity_rdv_column_name: string | null;
  business_key: string | boolean | null;
  business_key_order?: number | null;
  business_key_for?: number | null;
  hash_key: string | boolean | null;
  pii: string | boolean | null;
  target_entity_rdv_column_transform: string | null;
  description: string | null;
  is_inherited?: boolean | null;
  inheritance_type?: string | null;
  is_locked?: boolean | null;
  inherited_from_target_entity_rdv_id?: number | null;
  inherited_from_target_entity_rdv_name?: string | null;
};

export type DataMartSourceColumnsResponse = {
  target_entity_rdv?: {
    target_entity_rdv_id: number;
    target_entity_rdv_name: string;
    table_construct?: string | null;
    target_schema?: string | null;
    subject_area?: string | null;
    ensemble?: string | null;
  } | null;
  parent_hub?: {
    hub_target_entity_rdv_id: number | null;
    hub_target_entity_rdv_name: string | null;
  } | null;
  items: DataMartSourceColumnRecord[];
};

const RDV_ALLOWED_TABLE_CONSTRUCT_KEYS = new Set([
  "linkastable",
  "hubastable",
  "satastable",
  "linkasview",
  "hubasview",
  "satasview"
]);

const RDV_SAT_TABLE_CONSTRUCT_KEYS = new Set(["satastable", "satasview"]);

// Customer-maintained DV objects carry the legacy 'MANUAL' construct. They are not
// generated, but a dimension may legitimately source from one (SGDS loads several
// dimensions from bdv views and hand-built rdv tables), so they belong in the picker.
const RDV_CUSTOM_TABLE_CONSTRUCT_KEYS = new Set(["manual"]);

const normalizeRdvTableConstruct = (value?: string | null) =>
  value ? value.replace(/[\s_-]/g, "").trim().toLowerCase() : "";

export const isAllowedRdvTableConstruct = (value?: string | null) =>
  RDV_ALLOWED_TABLE_CONSTRUCT_KEYS.has(normalizeRdvTableConstruct(value));

export const isSatRdvTableConstruct = (value?: string | null) =>
  RDV_SAT_TABLE_CONSTRUCT_KEYS.has(normalizeRdvTableConstruct(value));

export const isCustomRdvTableConstruct = (value?: string | null) =>
  RDV_CUSTOM_TABLE_CONSTRUCT_KEYS.has(normalizeRdvTableConstruct(value));

/** Anything a Data Mart entity may point at: generated constructs plus custom objects. */
export const isSelectableDataMartSource = (value?: string | null) =>
  isAllowedRdvTableConstruct(value) || isCustomRdvTableConstruct(value);

const buildMetadataUrl = (path: string, params: URLSearchParams) => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const query = params.toString();
  return `${API_BASE_URL}${normalizedPath}${query ? `?${query}` : ""}`;
};

const buildTargetEntityDmGetUrl = (params: URLSearchParams) => {
  return buildMetadataUrl("/target-entity-dm-get", params);
};

const buildRelatedEntitiesUrl = (params: URLSearchParams) => {
  if (RELATED_ENTITIES_OVERRIDE) {
    const trimmed = RELATED_ENTITIES_OVERRIDE.replace(/\/$/, "");
    const joiner = trimmed.includes("?") ? "&" : "?";
    return `${trimmed}${joiner}${params.toString()}`;
  }
  return buildMetadataUrl("/fetch-related-entities", params);
};

type SaveTargetEntityPayload = Partial<ITargetEntityDmRecord>;

type LegacyTargetEntityDmRecord = ITargetEntityDmRecord & {
  target_entity_dm_rw_name?: string | null;
  target_entity_dm_rw_source_filter?: string | null;
  exclude_automation?: boolean | null;
};

type LegacyTargetEntityDmColumnRecord = ITargetEntityDmColumnRecord & {
  target_entity_dm_rw_column_name?: string | null;
  target_entity_dm_rw_column_transform?: string | null;
};

type LegacyAutomationExclusionField = {
  exclude_automation?: boolean | null;
};

const buildActionUrl = (path: string, environmentId: string | number, customerId: string | number) => {
  const params = new URLSearchParams({
    environment_id: String(environmentId),
    customer_id: String(customerId)
  });
  return buildMetadataUrl(path, params);
};

export function generateDataMartSqlPreview(
  environmentId: string | number,
  dataMartName: string,
  fabricLakehouseName?: string | null,
  options?: { shouldGenerateInserts?: boolean; fileSystem?: string | null }
) {
  const trimmedDataMart = dataMartName.trim();
  if (!trimmedDataMart) {
    return Promise.reject(new Error("Select a data mart before generating SQL preview."));
  }

  const headers: Record<string, string> = {
    Accept: "application/json",
    "Data-Mart": trimmedDataMart,
    "Should-Generate-Inserts": options?.shouldGenerateInserts ? "true" : "false",
    // Preview only: report a failing entity in generation_report and keep generating
    // the rest. Deploy/load callers omit this and still fail loudly.
    "Allow-Partial-Generation": "true"
  };

  const trimmedLakehouse = fabricLakehouseName?.trim();
  if (trimmedLakehouse) {
    headers["Fabric-Lakehouse-Name"] = trimmedLakehouse;
  }

  // Storage container for LAKEHOUSE targets; feeds LOCATION '/mnt/{file_system}/...'.
  const trimmedFileSystem = options?.fileSystem?.trim();
  if (trimmedFileSystem) {
    headers["File-System"] = trimmedFileSystem;
  }

  return typedRequest<DataMartSqlPreviewResponse>(
    buildMetadataUrl(`/environments/${environmentId}/generate_data_mart`, new URLSearchParams()),
    {
      method: "POST",
      headers
    },
    {
      options: { isUrlExclusive: true }
    }
  );
}

export type DqTestSqlPreviewResponse = {
  status?: string;
  sql: string;
};

/**
 * Generate the Data Quality test SQL preview for a data mart. Mirrors generateDataMartSqlPreview but
 * hits the DQ endpoint, which takes the data mart name in the JSON body `subject_area` field (the
 * backend maps `subject_area` -> DataMart.data_mart) and returns { status, sql }.
 */
export function generateDqTestDataMartSqlPreview(
  environmentId: string | number,
  dataMartName: string
) {
  const trimmedDataMart = dataMartName.trim();
  if (!trimmedDataMart) {
    return Promise.reject(new Error("Select a data mart before generating DQ test preview."));
  }
  return api.post<DqTestSqlPreviewResponse>(
    buildMetadataUrl(`/environments/${environmentId}/generate_dq_test_data_mart`, new URLSearchParams()),
    { subject_area: trimmedDataMart },
    getAccessToken(),
    { options: { isUrlExclusive: true } }
  );
}

const normalizeContextValue = (value?: string | number | null) => {
  if (value === undefined || value === null) {
    return null;
  }
  return typeof value === "string" ? Number(value) : value;
};

const stripLegacyTargetEntityFields = (
  record: (SaveTargetEntityPayload | LegacyTargetEntityDmRecord) & LegacyAutomationExclusionField
): SaveTargetEntityPayload | LegacyTargetEntityDmRecord => {
  const { exclude_automation: _excludeAutomation, ...nextRecord } = record;
  return nextRecord;
};

const normalizeTargetEntityRecord = (
  record: LegacyTargetEntityDmRecord
): ITargetEntityDmRecord => {
  const normalizedRecord = stripLegacyTargetEntityFields(record) as LegacyTargetEntityDmRecord;
  return {
    ...normalizedRecord,
    target_entity_dm_report_view_name:
      normalizedRecord.target_entity_dm_report_view_name ??
      normalizedRecord.target_entity_dm_rw_name ??
      null,
    target_entity_dm_report_view_source_filter:
      normalizedRecord.target_entity_dm_report_view_source_filter ??
      normalizedRecord.target_entity_dm_rw_source_filter ??
      null
  };
};

const normalizeTargetEntityColumnRecord = (
  record: LegacyTargetEntityDmColumnRecord
): ITargetEntityDmColumnRecord => ({
  ...record,
  target_entity_dm_report_view_column_name:
    record.target_entity_dm_report_view_column_name ??
    record.target_entity_dm_rw_column_name ??
    null,
  target_entity_dm_report_view_column_transform:
    record.target_entity_dm_report_view_column_transform ??
    record.target_entity_dm_rw_column_transform ??
    null
});

const buildDataMartRecords = (entities: ITargetEntityDmRecord[]): IDataMartRecord[] => {
  if (!entities.length) {
    return [];
  }
  const registry = new Map<string, IDataMartRecord>();
  entities.forEach((entity) => {
    const key = (entity.datamart ?? "").trim() || `DATA_MART_${registry.size + 1}`;
    if (!registry.has(key)) {
      registry.set(key, {
        data_mart_id: entity.data_mart_id ?? null,
        data_mart: key,
        environment_id: entity.environment_id,
        customer_id: entity.customer_id
      });
    }
  });
  return Array.from(registry.values());
};

const buildEntityColumns = (
  targetEntities: ITargetEntityDmRecord[]
): ITargetEntityDmColumnRecord[] => {
  const columns: ITargetEntityDmColumnRecord[] = [];
  targetEntities.forEach((entity) => {
    if (entity?.target_entity_dm_name) {
      const columnRecord: ITargetEntityDmColumnRecord = {
        target_entity_dm_column_id: Number(`${Date.now()}${Math.floor(Math.random() * 1000)}`),
        target_entity_dm_id: entity.target_entity_dm_id,
        target_entity_dm_column_name: entity.target_entity_dm_name,
        target_entity_dm_report_view_column_name:
          entity.target_entity_dm_report_view_name ?? entity.target_entity_dm_name,
        description: entity.description ?? null,
        target_entity_dm_column_transform: null,
        target_entity_dm_report_view_column_transform: null,
        dimension_key: null,
        dimension_attribute: null,
        pii: null,
        is_active: true,
        created_at: entity.created_at,
        updated_at: entity.updated_at,
        customer_id: entity.customer_id,
        environment_id: entity.environment_id
      };
      columns.push(columnRecord);
    }
  });
  return columns;
};

export async function fetchTargetEntityDmColumns(
  environmentId?: string | number | null,
  customerId?: string | number | null,
  targetEntityRdvId?: string | number | null
): Promise<ITargetEntityDmColumnRecord[]> {
  const resolvedEnvironmentId = normalizeContextValue(environmentId ?? getEnvironmentId());
  if (resolvedEnvironmentId === null) {
    throw new Error("Missing environment context for Data Mart columns.");
  }
  const resolvedCustomerId = normalizeContextValue(customerId ?? getCustomerId());
  if (resolvedCustomerId === null) {
    throw new Error("Missing customer context for Data Mart columns.");
  }
  const resolvedTargetEntityRdvId = normalizeContextValue(targetEntityRdvId);
  const params = new URLSearchParams({
    environment_id: String(resolvedEnvironmentId),
    customer_id: String(resolvedCustomerId)
  });
  if (resolvedTargetEntityRdvId !== null) {
    params.append("target_entity_rdv_id", String(resolvedTargetEntityRdvId));
  }
  const url = buildMetadataUrl("/target-entity-dm-columns", params);
  const response =
    (await api.get<
      LegacyTargetEntityDmColumnRecord[] | { items?: LegacyTargetEntityDmColumnRecord[] }
    >(
      url,
      getAccessToken(),
      {
        options: { isUrlExclusive: true }
      }
  )) ?? [];
  if (Array.isArray(response)) {
    return response.map(normalizeTargetEntityColumnRecord);
  }
  if (!Array.isArray(response?.items)) {
    return [];
  }
  return response.items.map(normalizeTargetEntityColumnRecord);
}

export async function fetchTargetEntityDmSourceColumns(
  environmentId?: string | number | null,
  customerId?: string | number | null,
  targetEntityRdvId?: string | number | null
): Promise<DataMartSourceColumnsResponse> {
  const resolvedEnvironmentId = normalizeContextValue(environmentId ?? getEnvironmentId());
  if (resolvedEnvironmentId === null) {
    throw new Error("Missing environment context for Data Mart source columns.");
  }
  const resolvedCustomerId = normalizeContextValue(customerId ?? getCustomerId());
  if (resolvedCustomerId === null) {
    throw new Error("Missing customer context for Data Mart source columns.");
  }
  const resolvedTargetEntityRdvId = normalizeContextValue(targetEntityRdvId);
  if (resolvedTargetEntityRdvId === null) {
    return { items: [] };
  }
  const params = new URLSearchParams({
    environment_id: String(resolvedEnvironmentId),
    customer_id: String(resolvedCustomerId),
    target_entity_rdv_id: String(resolvedTargetEntityRdvId)
  });
  const url = buildMetadataUrl("/target-entity-dm-source-columns", params);
  const response =
    (await api.get<DataMartSourceColumnsResponse>(url, getAccessToken(), {
      options: { isUrlExclusive: true }
    })) ?? { items: [] };
  return {
    ...response,
    items: Array.isArray(response.items) ? response.items : []
  };
}

export async function fetchDataMartColumnAutocomplete(
  environmentId?: string | number | null,
  customerId?: string | number | null,
  targetEntityRdvId?: string | number | null
): Promise<DataMartColumnAutocompleteResponse> {
  const resolvedEnvironmentId = normalizeContextValue(environmentId ?? getEnvironmentId());
  if (resolvedEnvironmentId === null) {
    throw new Error("Missing environment context for Data Mart autocomplete.");
  }
  const resolvedCustomerId = normalizeContextValue(customerId ?? getCustomerId());
  if (resolvedCustomerId === null) {
    throw new Error("Missing customer context for Data Mart autocomplete.");
  }
  const resolvedTargetEntityRdvId = normalizeContextValue(targetEntityRdvId);
  if (resolvedTargetEntityRdvId === null) {
    return {};
  }

  const params = new URLSearchParams({
    environment_id: String(resolvedEnvironmentId),
    customer_id: String(resolvedCustomerId),
    target_entity_rdv_id: String(resolvedTargetEntityRdvId)
  });
  const url = buildMetadataUrl("/target-entity-dm-column-autocomplete", params);

  return (
    (await api.get<DataMartColumnAutocompleteResponse>(url, getAccessToken(), {
      options: { isUrlExclusive: true }
    })) ?? {}
  );
}

export async function fetchDataMartMetadata(
  environmentId?: string | number | null,
  customerId?: string | number | null
): Promise<DataMartMetadataResponse> {
  const resolvedEnvironmentId = environmentId ?? getEnvironmentId();
  const resolvedCustomerId = customerId ?? getCustomerId();

  if (!resolvedEnvironmentId) {
    throw new Error("Missing environment context for Data Mart metadata.");
  }

  const params = new URLSearchParams({
    environment_id: String(resolvedEnvironmentId)
  });

  if (resolvedCustomerId) {
    params.append("customer_id", String(resolvedCustomerId));
  }

  const url = buildTargetEntityDmGetUrl(params);

  try {
    const targetEntitiesResponse =
      (await api.get<LegacyTargetEntityDmRecord[]>(url, getAccessToken(), {
        options: { isUrlExclusive: true }
      })) ?? [];
    const targetEntities = Array.isArray(targetEntitiesResponse)
      ? targetEntitiesResponse.map(normalizeTargetEntityRecord)
      : [];
    let targetEntityColumns: ITargetEntityDmColumnRecord[] = [];
    const canFetchColumns = Boolean(resolvedCustomerId);
    let columnFetchFailed = false;
    if (canFetchColumns) {
      try {
        targetEntityColumns = await fetchTargetEntityDmColumns(
          resolvedEnvironmentId,
          resolvedCustomerId
        );
      } catch (columnError) {
        columnFetchFailed = true;
        console.warn("Unable to load Data Mart columns from API.", columnError);
      }
    } else {
      columnFetchFailed = true;
    }
    const dataMarts = buildDataMartRecords(targetEntities);
    if (columnFetchFailed) {
      targetEntityColumns = buildEntityColumns(targetEntities);
    }
    return {
      data_marts: dataMarts,
      target_entities: targetEntities,
      target_entity_columns: targetEntityColumns
    };
  } catch (error) {
    console.warn("Unable to load Data Mart metadata from API.", error);
    return {
      data_marts: [],
      target_entities: [],
      target_entity_columns: []
    };
  }
}

export async function saveTargetEntityMetadata(
  payloads: SaveTargetEntityPayload[]
): Promise<ITargetEntityDmRecord[]> {
  if (!payloads.length) {
    return [];
  }
  const resolvedEnvironmentId = normalizeContextValue(getEnvironmentId());
  const resolvedCustomerId = normalizeContextValue(getCustomerId());

  if (resolvedEnvironmentId === null || resolvedCustomerId === null) {
    throw new Error("Missing environment/customer context for saving Data Mart metadata.");
  }

  const requestBody: SaveTargetEntityPayload[] = payloads.map((payload) => {
    const sanitizedPayload = stripLegacyTargetEntityFields(payload) as SaveTargetEntityPayload;
    return {
      ...sanitizedPayload,
      environment_id: resolvedEnvironmentId,
      customer_id: resolvedCustomerId,
      data_mart_id: sanitizedPayload.data_mart_id ?? null
    };
  });
  const middlewareOptions = {
    options: { isUrlExclusive: true }
  };

  // SPLIT BY ITEM, never by batch.
  //
  // This used to pick one endpoint for the whole request with
  // `requestBody.every((payload) => payload.target_entity_dm_id)` — so a batch containing ONE new
  // entity sent every OTHER entity to /target-entity-dm-create as well, and the create endpoint
  // inserts unconditionally without honouring an id. Saving a new dimension alongside twenty
  // existing ones therefore duplicated all twenty.
  //
  // That is where the production duplicates came from: 47 groups and 170 extra rows, created in
  // one-second bursts from 2024-11 through 2026-08, each burst one save.
  const toUpdate = requestBody.filter((payload) => payload.target_entity_dm_id);
  const toCreate = requestBody.filter((payload) => !payload.target_entity_dm_id);

  const send = async (path: string, body: SaveTargetEntityPayload[], method: "put" | "post") => {
    if (!body.length) {
      return [] as LegacyTargetEntityDmRecord[];
    }
    const url = buildActionUrl(path, resolvedEnvironmentId, resolvedCustomerId);
    const response =
      method === "put"
        ? await api.put<LegacyTargetEntityDmRecord | LegacyTargetEntityDmRecord[]>(
          url,
          body,
          getAccessToken(),
          middlewareOptions
        )
        : await api.post<LegacyTargetEntityDmRecord | LegacyTargetEntityDmRecord[]>(
          url,
          body,
          getAccessToken(),
          middlewareOptions
        );
    return Array.isArray(response) ? response : [response];
  };

  // Updates first: if the create half fails, what already existed is left consistent rather than
  // half-rewritten.
  const updated = await send("/target-entity-dm-update", toUpdate, "put");
  const created = await send("/target-entity-dm-create", toCreate, "post");

  return [...updated, ...created].map(normalizeTargetEntityRecord);
}

export async function loadDataMartModel(params?: BuildDataMartModelParams) {
  if (params) {
    return buildDataMartModel(params);
  }
  const metadata = await fetchDataMartMetadata();
  return buildDataMartModel({
    dataMarts: metadata?.data_marts ?? [],
    targetEntities: metadata?.target_entities ?? [],
    targetEntityColumns: metadata?.target_entity_columns ?? []
  });
}

export type DataMartColumnCreatePayload = {
  target_entity_dm_id: number;
  target_entity_dm_column_name: string;
  target_entity_dm_report_view_column_name: string;
  description?: string | null;
  target_entity_dm_column_transform?: string | null;
  target_entity_dm_report_view_column_transform?: string | null;
  dimension_key?: string | boolean | null;
  dimension_attribute?: string | boolean | null;
  pii?: string | boolean | null;
  is_active?: boolean | null;
  target_entity_rdv_column_id?: number | null;
  target_entity_dm_column_id?: number | null;
};

export type DataMartColumnUpdatePayload = DataMartColumnCreatePayload & {
  target_entity_dm_column_id: number;
};

export async function createTargetEntityDmColumns(
  payloads: DataMartColumnCreatePayload[],
  environmentId?: string | number | null,
  customerId?: string | number | null
): Promise<ITargetEntityDmColumnRecord[]> {
  if (!payloads.length) {
    return [];
  }
  const resolvedEnvironmentId = normalizeContextValue(environmentId ?? getEnvironmentId());
  const resolvedCustomerId = normalizeContextValue(customerId ?? getCustomerId());
  if (resolvedEnvironmentId === null || resolvedCustomerId === null) {
    throw new Error("Missing environment/customer context for creating Data Mart columns.");
  }
  const createParams = new URLSearchParams({
    environment_id: String(resolvedEnvironmentId),
    customer_id: String(resolvedCustomerId)
  });
  const url = buildMetadataUrl("/target-entity-dm-columns/create", createParams);
  const response = await api.post<LegacyTargetEntityDmColumnRecord[]>(
    url,
    payloads,
    getAccessToken(),
    {
      options: { isUrlExclusive: true }
    }
  );
  return response.map(normalizeTargetEntityColumnRecord);
}

export async function updateTargetEntityDmColumns(
  payloads: DataMartColumnUpdatePayload[],
  environmentId?: string | number | null,
  customerId?: string | number | null
): Promise<ITargetEntityDmColumnRecord[]> {
  if (!payloads.length) {
    return [];
  }
  const resolvedEnvironmentId = normalizeContextValue(environmentId ?? getEnvironmentId());
  const resolvedCustomerId = normalizeContextValue(customerId ?? getCustomerId());
  if (resolvedEnvironmentId === null || resolvedCustomerId === null) {
    throw new Error("Missing environment/customer context for updating Data Mart columns.");
  }
  const updateParams = new URLSearchParams({
    environment_id: String(resolvedEnvironmentId),
    customer_id: String(resolvedCustomerId)
  });
  const url = buildMetadataUrl("/target-entity-dm-columns/update", updateParams);
  const response = await api.put<LegacyTargetEntityDmColumnRecord[]>(
    url,
    payloads,
    getAccessToken(),
    {
      options: { isUrlExclusive: true }
    }
  );
  return response.map(normalizeTargetEntityColumnRecord);
}

const getRelationshipContext = () => {
  const environmentId = normalizeContextValue(getEnvironmentId());
  const customerId = normalizeContextValue(getCustomerId());
  if (environmentId === null) {
    throw new Error("Missing environment context for relationship operation.");
  }
  if (customerId === null) {
    throw new Error("Missing customer context for relationship operation.");
  }
  return { environmentId, customerId };
};

const buildRelationshipParams = (factTargetEntityDmId?: number | null) => {
  const environmentId = normalizeContextValue(getEnvironmentId());
  const customerId = normalizeContextValue(getCustomerId());
  if (environmentId === null) {
    throw new Error("Missing environment context for loading relationships.");
  }
  const params = new URLSearchParams({
    environment_id: String(environmentId)
  });
  if (customerId !== null) {
    params.append("customer_id", String(customerId));
  }
  if (factTargetEntityDmId != null) {
    params.append("fact_target_entity_dm_id", String(factTargetEntityDmId));
  }
  return params;
};

type SaveRelationshipPayload = {
  fact_target_entity_dm_id: number;
  dimension_target_entity_dm_id: number;
  source_handle?: string | null;
  target_handle?: string | null;
  relationship_type?: string | null;
  join_condition?: string | null;
  relationship_status?: string | null;
};

export async function fetchTargetEntityRelationships(
  factTargetEntityDmId?: number | null
): Promise<ITargetEntityDmRelationshipRecord[]> {
  try {
    const params = buildRelationshipParams(factTargetEntityDmId);
    const url = buildMetadataUrl("/target-entity-dm-relationships", params);
    const relationships = await api.get<ITargetEntityDmRelationshipRecord[]>(
      url,
      getAccessToken(),
      {
        options: { isUrlExclusive: true }
      }
    );
    if (!Array.isArray(relationships)) {
      return [];
    }
    return relationships;
  } catch (error) {
    console.warn("Unable to load Data Mart relationships.", error);
    return [];
  }
}

export async function createTargetEntityRelationships(
  payloads: SaveRelationshipPayload[]
): Promise<ITargetEntityDmRelationshipRecord[]> {
  if (!payloads.length) {
    return [];
  }
  const { environmentId, customerId } = getRelationshipContext();
  const createParams = new URLSearchParams({
    environment_id: String(environmentId),
    customer_id: String(customerId)
  });
  const url = buildMetadataUrl("/target-entity-dm-relationships-create", createParams);
  return api.post<ITargetEntityDmRelationshipRecord[]>(
    url,
    payloads,
    getAccessToken(),
    {
      options: { isUrlExclusive: true }
    }
  );
}

export async function updateTargetEntityRelationships(
  payloads: SaveRelationshipPayload[]
): Promise<ITargetEntityDmRelationshipRecord[]> {
  if (!payloads.length) {
    return [];
  }
  const { environmentId, customerId } = getRelationshipContext();
  const url = buildActionUrl("/target-entity-dm-relationships-update", environmentId, customerId);
  return api.put<ITargetEntityDmRelationshipRecord[]>(
    url,
    payloads,
    getAccessToken(),
    {
      options: { isUrlExclusive: true }
    }
  );
}

export async function deleteTargetEntityRelationship(
  factTargetEntityDmId: number,
  dimensionTargetEntityDmId: number
) {
  const { environmentId, customerId } = getRelationshipContext();
  const params = new URLSearchParams({
    environment_id: String(environmentId),
    customer_id: String(customerId),
    fact_target_entity_dm_id: String(factTargetEntityDmId),
    dimension_target_entity_dm_id: String(dimensionTargetEntityDmId)
  });
  const url = buildMetadataUrl("/target-entity-dm-relationship-hard", params);
  await api.del(url, undefined, getAccessToken(), {
    options: { isUrlExclusive: true }
  });
}

export async function deleteTargetEntityRelationshipById(
  targetEntityDmRelationshipId: number
) {
  const { environmentId, customerId } = getRelationshipContext();
  const params = new URLSearchParams({
    environment_id: String(environmentId),
    customer_id: String(customerId),
    target_entity_dm_relationship_id: String(targetEntityDmRelationshipId)
  });
  const url = buildMetadataUrl("/target-entity-dm-relationship-hard-by-id", params);
  await api.del(url, undefined, getAccessToken(), {
    options: { isUrlExclusive: true }
  });
}

export async function deleteTargetEntityDm(
  targetEntityDmId: number,
  environmentId?: string | number | null,
  customerId?: string | number | null
) {
  const resolvedEnvironmentId = normalizeContextValue(environmentId ?? getEnvironmentId());
  const resolvedCustomerId = normalizeContextValue(customerId ?? getCustomerId());
  if (resolvedEnvironmentId === null || resolvedCustomerId === null) {
    throw new Error("Missing environment/customer context for deleting Data Mart entity.");
  }
  const params = new URLSearchParams({
    target_entity_dm_id: String(targetEntityDmId),
    environment_id: String(resolvedEnvironmentId),
    customer_id: String(resolvedCustomerId)
  });
  const url = buildMetadataUrl("/target-entity-dm-delete", params);
  await api.del(url, undefined, getAccessToken(), {
    options: { isUrlExclusive: true }
  });
}

export type DataMartLayoutUpdatePayload = {
  target_entity_dm_id: number;
  canvas_x?: number | null;
  canvas_y?: number | null;
};

export async function updateDataMartLayout(
  payloads: DataMartLayoutUpdatePayload[],
  environmentId?: string | number | null,
  customerId?: string | number | null
): Promise<ITargetEntityDmRecord[]> {
  if (!payloads.length) {
    return [];
  }
  const resolvedEnvironmentId = normalizeContextValue(environmentId ?? getEnvironmentId());
  const resolvedCustomerId = normalizeContextValue(customerId ?? getCustomerId());
  if (resolvedEnvironmentId === null || resolvedCustomerId === null) {
    throw new Error("Missing environment/customer context for saving Data Mart layout.");
  }
  const params = new URLSearchParams({
    environment_id: String(resolvedEnvironmentId),
    customer_id: String(resolvedCustomerId)
  });
  const url = buildMetadataUrl("/data-mart-layout-update", params);
  return api.put<ITargetEntityDmRecord[]>(url, payloads, getAccessToken(), {
    options: { isUrlExclusive: true }
  });
}

export async function fetchRelatedRdvEntities(
  environmentId?: string | number | null,
  customerId?: string | number | null
): Promise<TargetEntityRdvRecord[]> {
  const resolvedEnvironmentId = normalizeContextValue(environmentId ?? getEnvironmentId());
  if (resolvedEnvironmentId === null) {
    throw new Error("Missing environment context for related RDV entities.");
  }
  const resolvedCustomerId = normalizeContextValue(customerId ?? getCustomerId());
  if (resolvedCustomerId === null) {
    throw new Error("Missing customer context for related RDV entities.");
  }
  const params = new URLSearchParams({
    environment_id: String(resolvedEnvironmentId),
    customer_id: String(resolvedCustomerId)
  });
  const url = buildRelatedEntitiesUrl(params);
  const response = await api.get<
    TargetEntityRdvRecord[] | { target_entity_rdv?: TargetEntityRdvRecord[] }
  >(url, getAccessToken(), {
    options: { isUrlExclusive: true }
  });
  const records: TargetEntityRdvRecord[] = Array.isArray(response)
    ? response
    : Array.isArray(response?.target_entity_rdv)
      ? response.target_entity_rdv
      : [];
  // /fetch-related-entities returns one row per source-entity x mapping, so an entity with
  // several source mappings arrives several times (env7: 404 rows for 254 entities). The
  // picker is a list of entities, so collapse on identity before it reaches the dropdown.
  const seen = new Set<string>();
  const deduped = records.filter((record) => {
    const key =
      record.target_entity_rdv_id != null
        ? `id:${record.target_entity_rdv_id}`
        : `name:${(record.target_schema ?? "").toLowerCase()}.${(
            record.target_entity_rdv_name ?? ""
          ).toLowerCase()}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });

  return deduped
    .filter((record) => isSelectableDataMartSource(record.table_construct))
    // Drop migration leftovers: a row with no columns AND generation switched off can
    // never feed a dimension, and it is indistinguishable from the real entity by name
    // and construct alone (env7 has 8 such pairs). An entity that merely has generation
    // off — every customer-maintained object does — is kept.
    .filter(
      (record) =>
        (record.column_count ?? 0) > 0 || record.generate_model_entity !== false
    )
    .sort((left, right) =>
      left.target_entity_rdv_name.localeCompare(right.target_entity_rdv_name, undefined, {
        sensitivity: "base"
      })
    );
}

export async function promoteInternalGoldAssetToDataMart(payload: {
  source_entity_id: number;
  datamart: string;
  table_construct: string;
  data_mart_id?: number | null;
  target_entity_dm_name?: string | null;
  target_entity_dm_report_view_name?: string | null;
  target_schema?: string | null;
  description?: string | null;
  distribution_type?: string | null;
  distribution_key?: string | null;
  include_dq_test?: boolean | null;
  generate_model_entity?: boolean | null;
  is_active?: boolean | null;
}): Promise<ITargetEntityDmRecord> {
  const resolvedEnvironmentId = normalizeContextValue(getEnvironmentId());
  const resolvedCustomerId = normalizeContextValue(getCustomerId());
  if (resolvedEnvironmentId === null || resolvedCustomerId === null) {
    throw new Error("Missing environment/customer context for promoting internal Gold asset.");
  }
  const params = new URLSearchParams({
    environment_id: String(resolvedEnvironmentId),
    customer_id: String(resolvedCustomerId)
  });
  const url = buildMetadataUrl("/target-entity-dm-promote-internal-asset", params);
  const response = await api.post<LegacyTargetEntityDmRecord>(
    url,
    payload,
    getAccessToken(),
    {
      options: { isUrlExclusive: true }
    }
  );
  return normalizeTargetEntityRecord(response);
}
